<script lang="ts">
  import {
    DeviceFrame,
    defaultTheme,
  } from '@walku/core';
  import { onMount } from 'svelte';

  export let theme = defaultTheme;
  export let documentTitle = 'Survey';
  export let fullscreen = false;

  // Ensure theme has all properties by merging with default where needed
  $: mergedTheme = {
    ...defaultTheme,
    ...theme,
    // Explicitly preserve specific color properties to ensure they're not lost
    primaryColor: theme.primaryColor || defaultTheme.primaryColor,
    secondaryColor: theme.secondaryColor || defaultTheme.secondaryColor,
    tertiaryColor: theme.tertiaryColor || defaultTheme.tertiaryColor,
    successColor: theme.successColor || defaultTheme.successColor,
    dangerColor: theme.dangerColor || defaultTheme.dangerColor,
    warningColor: theme.warningColor || defaultTheme.warningColor,
    textColor: theme.textColor || defaultTheme.textColor,
    textColorDark: theme.textColorDark || defaultTheme.textColorDark,
    textOnPrimary: theme.textOnPrimary || defaultTheme.textOnPrimary,
    textOnSecondary: theme.textOnSecondary || defaultTheme.textOnSecondary,
    textOnTertiary: theme.textOnTertiary || defaultTheme.textOnTertiary,
    // Mode
    theme: theme.theme || defaultTheme.theme
  };

  // Determine the final font family string.
  // Use the theme's font family directly if provided, adding sans-serif fallback.
  // Otherwise, use the font family from the defaultTheme.
  $: finalFontFamily = theme?.fontFamily 
    ? `"${theme.fontFamily}", sans-serif` 
    : defaultTheme.fontFamily; // Ensure defaultTheme.fontFamily includes fallbacks if needed

  // Set theme mode on HTML element
  $: if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', mergedTheme.theme);
  }

  onMount(() => {
    document.documentElement.setAttribute('data-theme', mergedTheme.theme);
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  });
</script>

<svelte:head>
  <title>{documentTitle}</title>
</svelte:head>

<div
  class:fullscreen-mode={fullscreen}
  style="
    /* Apply font using the CSS variable */
    font-family: var(--font-family);
    --theme-primary-color: {mergedTheme.primaryColor};
    --theme-secondary-color: {mergedTheme.secondaryColor};
    --theme-tertiary-color: {mergedTheme.tertiaryColor};
    --theme-success-color: {mergedTheme.successColor};
    --theme-danger-color: {mergedTheme.dangerColor};
    --theme-warning-color: {mergedTheme.warningColor};
    --theme-text-color: {mergedTheme.theme === 'dark' ? mergedTheme.textColorDark : mergedTheme.textColor};
    --theme-text-color-light: {mergedTheme.textColorLight};
    --theme-text-color-dark: {mergedTheme.textColorDark};
    --theme-text-on-primary: {mergedTheme.textOnPrimary};
    --theme-text-on-secondary: {mergedTheme.textOnSecondary};
    --theme-text-on-tertiary: {mergedTheme.textOnTertiary};
    --theme-text-on-success: {mergedTheme.textOnSuccess};
    --theme-text-on-warning: {mergedTheme.textOnWarning};
    --theme-text-on-danger: {mergedTheme.textOnDanger};
    --bg-color: {mergedTheme.bgColor};
    --theme-bg-color: {mergedTheme.bgColor};
    --theme-bg-color-dark: {mergedTheme.bgColorDark};
    --theme-border-color: {mergedTheme.borderColor};
    --theme-border-color-dark: {mergedTheme.borderColorDark};
    --btn-primary-color: {mergedTheme.primaryColor};
    --btn-bg-color: {mergedTheme.primaryColor};
    /* Set the CSS variable using the calculated finalFontFamily */
    --font-family: {finalFontFamily};
    --theme-font-family: {finalFontFamily};
    --transition: {mergedTheme.transition};
    --radius: {mergedTheme.radius};
    --disabled-opacity: {mergedTheme.disabledOpacity};
    --spacing-x: 1.5rem;
    --spacing-y: 1.5rem;
  "
>
  <DeviceFrame {fullscreen}>
    <div class="device-screen">
      <div class="section-content">
        <main>
          <div class="screen-container">
            <div class="screen">
              <slot />
            </div>
          </div>
        </main>
      </div>
    </div>
  </DeviceFrame>
</div>

<style>
  .device-screen {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
  }

  .section-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .screen-container {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .screen {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
  }

  main {
    height: 100%;
    width: 100%;
    position: relative;
  }

  @media (orientation: landscape) and (min-width: 1024px) {
    :global(.screen) {
      max-width: 380px;
      margin: 0 auto;
    }
  }

  /* Override max-width when in fullscreen mode */
  :global(.fullscreen-mode .screen) {
    max-width: none !important;
  }
</style> 